import { logAnalyticsEvent } from '@/services/analyticsLogger';

export type AppleReadIntent = 'open_next_trip' | 'show_today_itinerary' | 'show_documents' | 'open_templates';
export type AppleActionIntent = 'start_navigation' | 'check_off_activity';
export type AppleIntent = AppleReadIntent | AppleActionIntent;
export type AppleWriteIntent = 'add_expense' | 'add_checklist_item' | 'check_off_activity';

export type AppleIntentRoute = {
  pathname: '/(tabs)' | '/(tabs)/plan' | '/(tabs)/more';
  params?: Record<string, string>;
};

export const APPLE_READ_INTENT_ROUTES: Record<AppleReadIntent, string> = {
  open_next_trip: '/(tabs)/plan',
  show_today_itinerary: '/(tabs)/plan?focus=today',
  show_documents: '/(tabs)/more?tab=documents',
  open_templates: '/(tabs)?openTemplates=1',
};

export const APPLE_DEFERRED_WRITE_INTENTS: AppleWriteIntent[] = ['add_expense', 'add_checklist_item'];

export const isAppleReadIntent = (value: string | undefined): value is AppleReadIntent =>
  value === 'open_next_trip' ||
  value === 'show_today_itinerary' ||
  value === 'show_documents' ||
  value === 'open_templates';

export const isAppleActionIntent = (value: string | undefined): value is AppleActionIntent =>
  value === 'start_navigation' || value === 'check_off_activity';

export const isAppleIntent = (value: string | undefined): value is AppleIntent =>
  isAppleReadIntent(value) || isAppleActionIntent(value);

export const resolveAppleReadIntentRoute = (
  intent: AppleReadIntent,
  opts: {
    tripId?: string;
    day?: string;
  } = {},
): AppleIntentRoute => {
  switch (intent) {
    case 'open_next_trip':
      return {
        pathname: '/(tabs)/plan',
        params: {
          ...(opts.tripId ? { tripId: opts.tripId } : {}),
          ...(opts.day ? { day: opts.day } : {}),
          appIntent: intent,
        },
      };
    case 'show_today_itinerary':
      return {
        pathname: '/(tabs)/plan',
        params: {
          ...(opts.tripId ? { tripId: opts.tripId } : {}),
          ...(opts.day ? { day: opts.day } : {}),
          focus: 'today',
          appIntent: intent,
        },
      };
    case 'show_documents':
      return {
        pathname: '/(tabs)/more',
        params: {
          tab: 'documents',
          ...(opts.tripId ? { tripId: opts.tripId } : {}),
          appIntent: intent,
        },
      };
    case 'open_templates':
      return {
        pathname: '/(tabs)',
        params: {
          openTemplates: '1',
          appIntent: intent,
        },
      };
  }
};

export const logAppleIntentResult = async (
  intent: AppleIntent,
  result: 'success' | 'failed',
  failureReason?: 'not_authenticated' | 'missing_trip' | 'missing_data' | 'unknown',
) => {
  await logAnalyticsEvent('apple_app_intent_invoked', {
    intent,
    result,
    failureReason: result === 'failed' ? failureReason ?? 'unknown' : undefined,
  });
};

export const logSpotlightOpen = async (entityType: 'trip' | 'template', entityId: string) => {
  await logAnalyticsEvent('apple_spotlight_item_opened', {
    entityType,
    entityId,
    source: 'spotlight',
  });
};
